import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import Icons from '@/components/icons/IconMap'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useHostContext } from '@/contexts/HostContext'
import { usePersonas } from '@/hooks/usePersonas'
import { useRelationships } from '@/hooks/useRelationships'
import wacnerMark from '../../../teams-manifest/color.png'
import './TopBar.css'

interface TopBarProps {
  copilotOpen?: boolean
  onToggleCopilot?: () => void
}

export const TopBar: React.FC<TopBarProps> = ({ copilotOpen = false, onToggleCopilot }) => {
  const { instance, accounts } = useMsal()
  const currentUser = useCurrentUser()
  const { appliedTheme, toggleTheme } = useHostContext()
  const location = useLocation()
  const { relationships } = useRelationships()
  const { personas } = usePersonas()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showSettingsMenu, setShowSettingsMenu] = useState(false)
  const [showAppMenu, setShowAppMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [search, setSearch] = useState('')
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (mobileSearchOpen) searchInputRef.current?.focus()
  }, [mobileSearchOpen])

  const isDark = appliedTheme === 'dark'
  const themeActionLabel = isDark ? 'Modo claro' : 'Modo oscuro'

  const account = accounts[0]
  const handleLogout = async () => {
    setShowUserMenu(false)
    await instance.logoutRedirect()
  }

  const apiUser = currentUser.data?.user
  const displayName = apiUser?.nombre ?? account?.name ?? 'Usuario'
  const displayEmail = apiUser?.email ?? account?.username ?? ''
  const profileLabel =
    currentUser.status === 'profile_pending'
      ? 'Perfil pendiente'
      : currentUser.status === 'not_configured'
        ? 'API pendiente'
        : currentUser.status === 'unauthorized'
          ? 'Sesión no autorizada'
          : account
            ? 'Sesión Microsoft'
            : 'Sin sesión'

  const normalizedSearch = search.trim().toLocaleLowerCase('es')
  const searchResults = useMemo(() => {
    if (normalizedSearch.length < 2) return { relationships: [], personas: [] }
    return {
      relationships: relationships
        .filter((relationship) => relationship.title.toLocaleLowerCase('es').includes(normalizedSearch))
        .sort((a, b) => a.title.localeCompare(b.title, 'es'))
        .slice(0, 5),
      personas: personas
        .filter((persona) => [persona.title, persona.email, persona.organizacionNombre].some((value) => value?.toLocaleLowerCase('es').includes(normalizedSearch)))
        .sort((a, b) => a.title.localeCompare(b.title, 'es'))
        .slice(0, 5),
    }
  }, [normalizedSearch, personas, relationships])

  const closeTransientMenus = () => {
    setShowAppMenu(false)
    setShowNotifications(false)
    setShowSettingsMenu(false)
  }

  const getInitials = (name: string) => {
    if (!name) return '?'
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <header className="topbar">
      {/* Col 1: Logo */}
      <div className="topbar__logo">
        <img className="topbar__logoMark" src={wacnerMark} alt="Wacner" />
        <div className="topbar__logoName">Wacner Sales</div>
        <div className="topbar__tag">Microsoft 365</div>
      </div>

      {/* Col 2: App selector */}
      <div className="topbar__appSelector">
        <button
          className="topbar__appButton"
          title="Navegación"
          aria-label="Navegación"
          aria-expanded={showAppMenu}
          onClick={() => {
            setShowAppMenu((visible) => !visible)
            setShowNotifications(false)
            setShowSettingsMenu(false)
          }}
        >
          <Icons.Apps size="md" />
        </button>
        {showAppMenu && (
          <div className="topbar__appMenu" role="menu" aria-label="Navegación de Sales">
            <Link to="/hoy" className={location.pathname === '/hoy' ? 'topbar__appMenuItem topbar__appMenuItem--active' : 'topbar__appMenuItem'} onClick={closeTransientMenus}>Hoy</Link>
            <Link to="/relaciones" className={location.pathname.startsWith('/relaciones') ? 'topbar__appMenuItem topbar__appMenuItem--active' : 'topbar__appMenuItem'} onClick={closeTransientMenus}>Empresas</Link>
            <Link to="/personas" className={location.pathname.startsWith('/personas') ? 'topbar__appMenuItem topbar__appMenuItem--active' : 'topbar__appMenuItem'} onClick={closeTransientMenus}>Personas</Link>
            <Link to="/iniciativas" className={location.pathname.startsWith('/iniciativas') ? 'topbar__appMenuItem topbar__appMenuItem--active' : 'topbar__appMenuItem'} onClick={closeTransientMenus}>Iniciativas</Link>
            <Link to="/configuracion" className={location.pathname === '/configuracion' ? 'topbar__appMenuItem topbar__appMenuItem--active' : 'topbar__appMenuItem'} onClick={closeTransientMenus}>Configuración</Link>
          </div>
        )}
      </div>

      {/* Col 3: Breadcrumb/Nav (reserved) */}
      <nav></nav>

      {/* Col 4: Search */}
      <div className={`topbar__search${mobileSearchOpen ? ' topbar__search--mobileOpen' : ''}`}>
        <button
          type="button"
          className="topbar__mobileSearchButton"
          aria-label="Abrir búsqueda"
          aria-expanded={mobileSearchOpen}
          onClick={() => setMobileSearchOpen(true)}
        >
          <Icons.Search size="sm" />
        </button>
        <Icons.Search size="sm" className="topbar__searchIcon" />
        <input
          ref={searchInputRef}
          type="search"
          placeholder="Buscar empresa o persona..."
          aria-label="Buscar empresa o persona"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={() => closeTransientMenus()}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setSearch('')
              setMobileSearchOpen(false)
              event.currentTarget.blur()
            }
          }}
        />
        {normalizedSearch.length >= 2 && (
          <div className="topbar__searchResults" role="listbox" aria-label="Resultados de búsqueda">
            {searchResults.relationships.length > 0 && (
              <div className="topbar__searchGroup">
                <span className="topbar__searchGroupLabel">Empresas</span>
                {searchResults.relationships.map((relationship) => (
                  <Link key={relationship.id} to={`/relaciones/${relationship.id}`} className="topbar__searchResult" onClick={() => { setSearch(''); setMobileSearchOpen(false) }}>
                    <strong>{relationship.title}</strong>
                    <span>{relationship.pais ?? 'País no registrado'}</span>
                  </Link>
                ))}
              </div>
            )}
            {searchResults.personas.length > 0 && (
              <div className="topbar__searchGroup">
                <span className="topbar__searchGroupLabel">Personas</span>
                {searchResults.personas.map((persona) => (
                  <Link key={persona.id} to={`/personas/${persona.id}`} className="topbar__searchResult" onClick={() => { setSearch(''); setMobileSearchOpen(false) }}>
                    <strong>{persona.title}</strong>
                    <span>{persona.organizacionNombre ?? persona.email ?? 'Sin empresa asociada'}</span>
                  </Link>
                ))}
              </div>
            )}
            {searchResults.relationships.length === 0 && searchResults.personas.length === 0 && (
              <p className="topbar__searchEmpty">No hay empresas ni personas coincidentes.</p>
            )}
          </div>
        )}
      </div>

      {/* Col 5: Copilot */}
      <div>
        <button
          className="topbar__iconButton topbar__copilotButton"
          title="Copilot Sales"
          aria-label="Copilot Sales"
          aria-pressed={copilotOpen}
          onClick={onToggleCopilot}
        >
          <span aria-hidden="true">✨</span>
          <span className="topbar__copilotLabel">Copilot</span>
        </button>
      </div>

      {/* Col 6: Right actions */}
      <div className="topbar__right" style={{ position: 'relative' }}>
        <button
          className="topbar__iconButton topbar__desktopUtility"
          title={themeActionLabel}
          aria-label={themeActionLabel}
          aria-pressed={isDark}
          onClick={toggleTheme}
        >
          {isDark ? <Icons.ThemeLight size="md" /> : <Icons.ThemeDark size="md" />}
        </button>

        <button
          className="topbar__iconButton"
          title="Notificaciones"
          aria-label="Notificaciones"
          aria-expanded={showNotifications}
          onClick={() => {
            setShowNotifications((visible) => !visible)
            setShowAppMenu(false)
            setShowSettingsMenu(false)
          }}
        >
          <Icons.Alert size="md" />
        </button>
        {showNotifications && (
          <div className="topbar__notifications" role="status">
            <strong>Trabajo diario</strong>
            <span>Revisa compromisos, tareas Microsoft To Do y reuniones próximas.</span>
            <Link to="/hoy" onClick={closeTransientMenus}>Abrir Hoy</Link>
          </div>
        )}

        <div className="topbar__desktopUtility" style={{ position: 'relative' }}>
          <button
            className="topbar__iconButton"
            title="Administración"
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
          >
            <Icons.Settings size="md" />
          </button>
          {showSettingsMenu && (
            <div className="topbar__settingsMenu">
              <Link to="/configuracion" className="topbar__settingsMenuItem">
                <Icons.Configuracion size="sm" />
                <span>Configuración</span>
              </Link>
            </div>
          )}
        </div>

        <div
          className="topbar__user"
          onClick={() => setShowUserMenu(!showUserMenu)}
          title={displayName}
        >
          {getInitials(displayName)}
        </div>

        {showUserMenu && (
          <div className="topbar__userMenu">
            <div className="topbar__userMenuContent">
              <div className="topbar__userName">{displayName}</div>
              <div className="topbar__userEmail">{displayEmail}</div>
              {profileLabel && <div className="topbar__profileStatus">{profileLabel}</div>}
            </div>
            <div className="topbar__userMenuDivider"></div>
            <button onClick={handleLogout} className="topbar__userMenuButton">
              <Icons.SignOut size="sm" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
